// Textured billboard particles with true per-instance alpha.
//
// The existing particles.ts uses InstancedMesh, where opacity is a *material* uniform, so it
// cannot fade individual particles — it fakes fade by shrinking scale, and is stuck on untextured
// tetrahedra. That is fine for sparks and debris and stays as-is.
//
// This adds the layer it cannot express: soft smoke, embers, fire, shockwaves and trails. An
// InstancedBufferGeometry with per-instance attributes (position, scale, rotation, colour, alpha,
// atlas cell) plus a small ShaderMaterial gives each particle its own opacity in a single draw
// call. Two pools total — one additive, one alpha-blended — so this costs 2 draw calls.
//
// Note there is no depth-based soft-particle fade: EffectComposer ping-pongs between two render
// targets that each own a cloned depth texture, so sampling "the" depth texture reads the wrong
// buffer on alternating frames. The atlas sprites are soft-edged instead, which covers most of
// the same ground for billboards lying near the ground plane.

import * as THREE from 'three';
import { ATLAS_COLS, ATLAS_ROWS, getSpriteAtlas } from './proceduralTextures';

export interface BillboardSpawn {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** seconds */
  ttl: number;
  size: number;
  /** world units per second added to size over life; negative shrinks */
  growth: number;
  color: THREE.Color;
  /** peak opacity, reached at `fadeIn` through life then eased to 0 */
  alpha: number;
  /** atlas cell index (see SPRITE) */
  cell: number;
  rotation: number;
  spin: number;
  /** upward drift/buoyancy in u/s^2; negative falls */
  gravity: number;
  /** velocity retained per second, 0..1; lower values settle faster */
  drag: number;
}

interface Particle extends BillboardSpawn {
  age: number;
  maxTtl: number;
  baseSize: number;
}

const VERTEX = /* glsl */ `
  attribute vec3 iOffset;
  attribute float iScale;
  attribute float iRotation;
  attribute vec3 iColor;
  attribute float iAlpha;
  attribute float iCell;

  uniform vec2 atlasGrid;

  varying vec2 vUv;
  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    // Camera-facing basis pulled from the view matrix, so quads always face the viewer without
    // any per-instance CPU orientation work.
    vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
    vec3 up    = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);

    float s = sin(iRotation);
    float c = cos(iRotation);
    vec2 corner = vec2(
      position.x * c - position.y * s,
      position.x * s + position.y * c
    ) * iScale;

    vec3 world = iOffset + right * corner.x + up * corner.y;

    // Sub-rectangle of the atlas for this instance's cell.
    float col = mod(iCell, atlasGrid.x);
    float row = floor(iCell / atlasGrid.x);
    vUv = (uv + vec2(col, row)) / atlasGrid;

    vColor = iColor;
    vAlpha = iAlpha;
    gl_Position = projectionMatrix * viewMatrix * vec4(world, 1.0);
  }
`;

const FRAGMENT = /* glsl */ `
  uniform sampler2D atlas;
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    // The atlas is a luminance mask, so red carries the shape and instances tint it freely.
    float mask = texture2D(atlas, vUv).r;
    float alpha = mask * vAlpha;
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(vColor * mask, alpha);
  }
`;

class BillboardPool {
  readonly mesh: THREE.Mesh;
  private readonly live: Particle[] = [];
  private readonly max: number;
  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly offsets: THREE.InstancedBufferAttribute;
  private readonly scales: THREE.InstancedBufferAttribute;
  private readonly rotations: THREE.InstancedBufferAttribute;
  private readonly colors: THREE.InstancedBufferAttribute;
  private readonly alphas: THREE.InstancedBufferAttribute;
  private readonly cells: THREE.InstancedBufferAttribute;
  private density = 1;

  constructor(scene: THREE.Scene, max: number, additive: boolean) {
    this.max = max;

    const quad = new THREE.PlaneGeometry(1, 1);
    this.geometry = new THREE.InstancedBufferGeometry();
    this.geometry.index = quad.index;
    this.geometry.attributes.position = quad.attributes.position;
    this.geometry.attributes.uv = quad.attributes.uv;
    quad.dispose();

    const alloc = (size: number) =>
      new THREE.InstancedBufferAttribute(new Float32Array(max * size), size).setUsage(THREE.DynamicDrawUsage);
    this.offsets = alloc(3);
    this.scales = alloc(1);
    this.rotations = alloc(1);
    this.colors = alloc(3);
    this.alphas = alloc(1);
    this.cells = alloc(1);
    this.geometry.setAttribute('iOffset', this.offsets);
    this.geometry.setAttribute('iScale', this.scales);
    this.geometry.setAttribute('iRotation', this.rotations);
    this.geometry.setAttribute('iColor', this.colors);
    this.geometry.setAttribute('iAlpha', this.alphas);
    this.geometry.setAttribute('iCell', this.cells);
    this.geometry.instanceCount = 0;

    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      uniforms: {
        atlas: { value: getSpriteAtlas() },
        atlasGrid: { value: new THREE.Vector2(ATLAS_COLS, ATLAS_ROWS) },
      },
      transparent: true,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      // Rendered into the HDR composer target; OutputPass tone-maps everything, so this shader
      // must not tone-map again.
      toneMapped: false,
    });

    this.mesh = new THREE.Mesh(this.geometry, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = additive ? 8 : 7;
    scene.add(this.mesh);
  }

  setDensity(density: number): void {
    this.density = Math.max(0, density);
  }

  spawn(spec: BillboardSpawn, count = 1): void {
    const scaled = Math.round(count * this.density);
    for (let i = 0; i < scaled; i++) {
      if (this.live.length >= this.max) return;
      this.live.push({ ...spec, age: 0, maxTtl: spec.ttl, baseSize: spec.size });
    }
  }

  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i];
      p.age += dt;
      if (p.age >= p.maxTtl) {
        // Swap-remove: order is irrelevant for additive/soft particles and this stays O(1).
        this.live[i] = this.live[this.live.length - 1];
        this.live.pop();
        continue;
      }
      const decay = Math.pow(p.drag, dt);
      p.vx *= decay;
      p.vz *= decay;
      p.vy = p.vy * decay + p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      p.rotation += p.spin * dt;
    }

    const count = Math.min(this.live.length, this.max);
    for (let i = 0; i < count; i++) {
      const p = this.live[i];
      const life = p.age / p.maxTtl;
      this.offsets.array[i * 3] = p.x;
      this.offsets.array[i * 3 + 1] = p.y;
      this.offsets.array[i * 3 + 2] = p.z;
      this.scales.array[i] = Math.max(0.001, p.baseSize + p.growth * life);
      this.rotations.array[i] = p.rotation;
      this.colors.array[i * 3] = p.color.r;
      this.colors.array[i * 3 + 1] = p.color.g;
      this.colors.array[i * 3 + 2] = p.color.b;
      // Quick ramp in, long ease out — the shape that reads as smoke rather than a blink.
      const fadeIn = Math.min(1, life / 0.15);
      const fadeOut = 1 - Math.pow(life, 1.7);
      this.alphas.array[i] = p.alpha * fadeIn * Math.max(0, fadeOut);
      this.cells.array[i] = p.cell;
    }
    this.geometry.instanceCount = count;
    if (count > 0) {
      this.offsets.needsUpdate = true;
      this.scales.needsUpdate = true;
      this.rotations.needsUpdate = true;
      this.colors.needsUpdate = true;
      this.alphas.needsUpdate = true;
      this.cells.needsUpdate = true;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}

/** Additive pool is generous: embers and trails are the highest-count emitters. */
const MAX_ADDITIVE = 2048;
const MAX_SOFT = 1024;

export class BillboardSystem {
  private additive: BillboardPool;
  private soft: BillboardPool;

  constructor(scene: THREE.Scene) {
    this.additive = new BillboardPool(scene, MAX_ADDITIVE, true);
    this.soft = new BillboardPool(scene, MAX_SOFT, false);
  }

  setQuality(quality: 'low' | 'medium' | 'high' | 'ultra', reducedMotion: boolean): void {
    // Mirrors ParticleSystem.setQuality so both layers thin out together.
    const base = quality === 'low' ? 0.35 : quality === 'medium' ? 0.65 : quality === 'ultra' ? 1.25 : 1;
    const density = reducedMotion ? base * 0.45 : base;
    this.additive.setDensity(density);
    this.soft.setDensity(density);
  }

  emitAdditive(spec: BillboardSpawn, count = 1): void {
    this.additive.spawn(spec, count);
  }

  emitSoft(spec: BillboardSpawn, count = 1): void {
    this.soft.spawn(spec, count);
  }

  update(dt: number): void {
    this.additive.update(dt);
    this.soft.update(dt);
  }

  dispose(): void {
    this.additive.dispose();
    this.soft.dispose();
  }
}
